import { TFunction } from "i18next";

/**
 * Keys of the stages of campaign setup, in the order they appear in the campaign wizard.
 */
export type CampaignGuideStageKey = 'campaign' | 'questionnaire' | 'people' | 'schedule' | 'review';

/**
 * A field or option a user will find on a campaign setup stage.
 */
export interface CampaignGuideStageField {
    name: string,
    description: string,
}

/**
 * A row of the example screen shown alongside a stage, to give an idea of what the user will see.
 */
export interface CampaignGuideStagePreviewRow {
    text: string,
    isFilledIn: boolean,
}

/**
 * Description of one stage of campaign setup for the campaign guide.
 */
export interface CampaignGuideStage {
    key: CampaignGuideStageKey,
    label: string,
    title: string,
    introduction: string,
    fields: Array<CampaignGuideStageField>,
    tip: string,
    previewRows: Array<CampaignGuideStagePreviewRow>,
}

/**
 * Returns the stages of campaign setup explained by the campaign guide, in wizard order.
 */
export function getCampaignGuideStages(t: TFunction): Array<CampaignGuideStage> {
    return [
        {
            key: 'campaign',
            label: t('campaignGuide.stages.campaign.label', 'Campaign'),
            title: t('campaignGuide.stages.campaign.title', 'Campaign Information'),
            introduction: t('campaignGuide.stages.campaign.introduction', 'Set the identity of the campaign: what it\'s called, whether it\'s linked to a learning event, and what that event cost, so ROI can be calculated later.'),
            fields: [
                {
                    name: t('campaignGuide.stages.campaign.fields.title.name', 'Campaign title & instructions'),
                    description: t('campaignGuide.stages.campaign.fields.title.description', 'The name delegates see, plus optional instructions explaining why they\'re being asked to complete it.'),
                },
                {
                    name: t('campaignGuide.stages.campaign.fields.learningEvent.name', 'Learning event or resource'),
                    description: t('campaignGuide.stages.campaign.fields.learningEvent.description', 'Toggle on to link this campaign to a specific event, then give it a name and a type (Training Course/Workshop, eLearning, Coaching, Conference, Development Programme, Book, Video, and others).'),
                },
                {
                    name: t('campaignGuide.stages.campaign.fields.provider.name', 'Learning provider & dates'),
                    description: t('campaignGuide.stages.campaign.fields.provider.description', 'Who ran it, and its start and end dates.'),
                },
                {
                    name: t('campaignGuide.stages.campaign.fields.cost.name', 'Cost'),
                    description: t('campaignGuide.stages.campaign.fields.cost.description', 'Total event/resource cost and cost per learner. This is what feeds ROI reporting later.'),
                },
            ],
            tip: t('campaignGuide.stages.campaign.tip', 'The cost fields are optional but do the heavy lifting. Without them IMPACTS can still show behaviour change, but it can\'t put a £ figure on ROI.'),
            previewRows: [
                { text: t('campaignGuide.stages.campaign.preview.title', 'Campaign title'), isFilledIn: true },
                { text: t('campaignGuide.stages.campaign.preview.learningEvent', 'Learning event? Yes'), isFilledIn: true },
                { text: t('campaignGuide.stages.campaign.preview.type', 'Type: Training Course/Workshop'), isFilledIn: true },
                { text: t('campaignGuide.stages.campaign.preview.cost', 'Cost per learner: £0.00'), isFilledIn: false },
            ],
        },
        {
            key: 'questionnaire',
            label: t('campaignGuide.stages.questionnaire.label', 'Questionnaire'),
            title: t('campaignGuide.stages.questionnaire.title', 'Questionnaire'),
            introduction: t('campaignGuide.stages.questionnaire.introduction', 'Build the assessment delegates complete pre- and post-training. The default template ships with seven sections already sequenced. Expand any of them to see or change what\'s inside.'),
            fields: [
                {
                    name: t('campaignGuide.stages.questionnaire.fields.defaultSections.name', 'Default template sections'),
                    description: t('campaignGuide.stages.questionnaire.fields.defaultSections.description', 'Intent, Make It Happen, Performance, Action, Continuous Learning, Transformation and Self-Coaching. Reorder them with Up/Down, or add your own.'),
                },
                {
                    name: t('campaignGuide.stages.questionnaire.fields.sectionHeading.name', 'Section heading & description'),
                    description: t('campaignGuide.stages.questionnaire.fields.sectionHeading.description', 'Click a section to rename it or add a description, which is shown to delegates under the heading.'),
                },
                {
                    name: t('campaignGuide.stages.questionnaire.fields.questions.name', 'Questions'),
                    description: t('campaignGuide.stages.questionnaire.fields.questions.description', 'Each section holds its own questions. Edit, reorder or delete any of them, or use Add Question for more.'),
                },
                {
                    name: t('campaignGuide.stages.questionnaire.fields.fromScratch.name', 'Build from scratch'),
                    description: t('campaignGuide.stages.questionnaire.fields.fromScratch.description', 'Skip the template entirely and add sections one at a time instead.'),
                },
            ],
            tip: t('campaignGuide.stages.questionnaire.tip', '"Intent" (before) and "Transformation" (after) are the pair that produce your before/after comparison, so check those two stay aligned if you edit the wording.'),
            previewRows: [
                { text: t('campaignGuide.stages.questionnaire.preview.intent', 'Intent'), isFilledIn: true },
                { text: t('campaignGuide.stages.questionnaire.preview.makeItHappen', 'Make It Happen'), isFilledIn: true },
                { text: t('campaignGuide.stages.questionnaire.preview.performance', 'Performance'), isFilledIn: true },
                { text: t('campaignGuide.stages.questionnaire.preview.addSection', '+ Add A Section'), isFilledIn: false },
            ],
        },
        {
            key: 'people',
            label: t('campaignGuide.stages.people.label', 'People'),
            title: t('campaignGuide.stages.people.title', 'Add People'),
            introduction: t('campaignGuide.stages.people.introduction', 'Add everyone this campaign applies to, manually for smaller groups or by import for bulk uploads.'),
            fields: [
                {
                    name: t('campaignGuide.stages.people.fields.add.name', 'Add Participants'),
                    description: t('campaignGuide.stages.people.fields.add.description', 'Manually add up to 5 people at a time: email, first and last name, department, and line manager name and email.'),
                },
                {
                    name: t('campaignGuide.stages.people.fields.import.name', 'Import Participants'),
                    description: t('campaignGuide.stages.people.fields.import.description', 'Upload a larger list in one go from an Excel (.xlsx), CSV or tab-delimited (.txt/.tsv) file.'),
                },
                {
                    name: t('campaignGuide.stages.people.fields.department.name', 'Department'),
                    description: t('campaignGuide.stages.people.fields.department.description', 'A dropdown, so results can be grouped and compared across teams later.'),
                },
                {
                    name: t('campaignGuide.stages.people.fields.manage.name', 'Manage All Participants'),
                    description: t('campaignGuide.stages.people.fields.manage.description', 'Review, edit or remove anyone already added to the campaign.'),
                },
            ],
            tip: t('campaignGuide.stages.people.tip', 'Line Manager Name and Email are optional, but leaving them blank is the main reason "Workplace Application" and manager-feedback reporting comes back incomplete.'),
            previewRows: [
                { text: t('campaignGuide.stages.people.preview.add', 'Add Participants'), isFilledIn: false },
                { text: t('campaignGuide.stages.people.preview.import', 'Import Participants'), isFilledIn: false },
                { text: t('campaignGuide.stages.people.preview.department', 'Department: Sales'), isFilledIn: true },
            ],
        },
        {
            key: 'schedule',
            label: t('campaignGuide.stages.schedule.label', 'Schedule'),
            title: t('campaignGuide.stages.schedule.title', 'Campaign Schedule'),
            introduction: t('campaignGuide.stages.schedule.introduction', 'Decide when the campaign goes live, when it closes, and how hard IMPACTS should chase incomplete responses.'),
            fields: [
                {
                    name: t('campaignGuide.stages.schedule.fields.startDate.name', 'Campaign start date'),
                    description: t('campaignGuide.stages.schedule.fields.startDate.description', 'Type any date, or use a quick-entry button: Launch Now, Tomorrow, Next Monday, 1 month from now or 2 months from now.'),
                },
                {
                    name: t('campaignGuide.stages.schedule.fields.endDate.name', 'Campaign end date'),
                    description: t('campaignGuide.stages.schedule.fields.endDate.description', 'The same idea. Type a date, or pick 1 day, Following Monday, 1 month or 2 months.'),
                },
                {
                    name: t('campaignGuide.stages.schedule.fields.reminders.name', 'Automatic reminders'),
                    description: t('campaignGuide.stages.schedule.fields.reminders.description', 'A toggle. When it\'s on, anyone who hasn\'t completed gets weekly reminders, moving to daily in the campaign\'s final week.'),
                },
            ],
            tip: t('campaignGuide.stages.schedule.tip', 'Reminders default to off. For anything measuring workplace change over weeks, switching them on is usually worth it. The daily push in the final week is what recovers stragglers.'),
            previewRows: [
                { text: t('campaignGuide.stages.schedule.preview.start', 'Start: Launch Now'), isFilledIn: true },
                { text: t('campaignGuide.stages.schedule.preview.end', 'End: 2 months'), isFilledIn: true },
                { text: t('campaignGuide.stages.schedule.preview.reminders', 'Reminders: Off'), isFilledIn: false },
            ],
        },
        {
            key: 'review',
            label: t('campaignGuide.stages.review.label', 'Review & Send'),
            title: t('campaignGuide.stages.review.title', 'Review'),
            introduction: t('campaignGuide.stages.review.introduction', 'A single-page summary of everything set up so far. It\'s the last chance to catch something before delegates receive anything.'),
            fields: [
                {
                    name: t('campaignGuide.stages.review.fields.name.name', 'Name'),
                    description: t('campaignGuide.stages.review.fields.name.description', 'The campaign title, plus a count of the sections and questions in the questionnaire.'),
                },
                {
                    name: t('campaignGuide.stages.review.fields.participants.name', 'Participants'),
                    description: t('campaignGuide.stages.review.fields.participants.description', 'The total number of people added.'),
                },
                {
                    name: t('campaignGuide.stages.review.fields.dates.name', 'Start Date & End Date'),
                    description: t('campaignGuide.stages.review.fields.dates.description', 'The dates set on the Schedule stage.'),
                },
                {
                    name: t('campaignGuide.stages.review.fields.learningUnit.name', 'Learning Unit'),
                    description: t('campaignGuide.stages.review.fields.learningUnit.description', 'The linked event, or "No associated Learning Unit" if that toggle was left off.'),
                },
            ],
            tip: t('campaignGuide.stages.review.tip', '"No associated Learning Unit" is a quiet but important flag. It means no cost data was attached, so ROI reporting won\'t have a £ figure to work from later.'),
            previewRows: [
                { text: t('campaignGuide.stages.review.preview.name', 'Name: Sales Leadership Programme'), isFilledIn: true },
                { text: t('campaignGuide.stages.review.preview.counts', 'with 7 sections and 25 questions'), isFilledIn: false },
                { text: t('campaignGuide.stages.review.preview.participants', 'Participants: 12'), isFilledIn: true },
                { text: t('campaignGuide.stages.review.preview.learningUnit', 'Learning Unit: none'), isFilledIn: false },
            ],
        },
    ];
}
